import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, RadioGroup, RadioTile } from '@eneco/sparky';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Header, Layout, NextButton } from '../../components';
import { useStepContent, useStepKey } from '../../hooks';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import { getIllustration } from '../../utils/getIllustration';
import { readChoiceOptions } from '../../utils/readChoiceOptions';
import { readContentSection } from '../../utils/readContentSection';

interface FormFields {
  [key: string]: string;
}
// TODO: Remove this component and it's related files once we replaced all with the GenericRadioChoiceStep or GenericCheckboxChoiceStep
export const GenericChoiceStep = () => {
  // This step won't re-render properly when used on multiple consecutive steps
  // without a key that changes per step page. See `useStepKey` for why the
  // rendering's `uid` is not that key.
  return <GenericChoice key={useStepKey()} />;
};

const GenericChoice: FC = () => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);
  const textualData = useStepContent('GenericChoiceStep');
  const { title, body } = readContentSection(textualData);
  const { options, requiredMessage } = readChoiceOptions(textualData);

  const genericFieldName = textualData?.name ?? '';

  const formSchema = yup.object({
    [genericFieldName]: yup.string().required(requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [genericFieldName]: flowContext[genericFieldName as keyof FlowContext]?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const mutatedGenericField = watch(genericFieldName);

  const showNonBlockingNotification =
    mutatedGenericField === textualData?.nonBlockingValue && textualData?.nonBlockingChoiceNotification?.content;
  const showBlockingNotification =
    mutatedGenericField === textualData?.blockingValue && textualData?.errorBlockingChoiceNotification?.content;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      let genericFieldValue: string | boolean | number = formData[genericFieldName];

      // convert genericFieldValue to appropriate type
      if (genericFieldValue.toLowerCase() === 'true' || genericFieldValue.toLowerCase() === 'false') {
        genericFieldValue = genericFieldValue.toLowerCase() === 'true';
      } else if (Number(genericFieldValue) || Number(genericFieldValue) === 0) {
        genericFieldValue = Number(genericFieldValue);
      }

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          [genericFieldName]: genericFieldValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{title}</Header.Title>
        {body && <RichText html={body} />}
      </Header>

      {textualData?.notification?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData.notification.variant}
          title={textualData.notification.title}
          text={<RichText html={textualData.notification.content} />}
        />
      )}

      <Controller
        control={control}
        name={genericFieldName}
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby={genericFieldName}
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors?.[genericFieldName]?.message}>
            {options.map(({ name, label, illustration }) => {
              const Illustration = getIllustration(illustration);

              return (
                <RadioTile
                  key={name}
                  value={name ?? ''}
                  icon={Illustration && <Illustration size="small" color="currentColor" />}>
                  {label ?? ''}
                </RadioTile>
              );
            })}
          </RadioGroup>
        )}
      />

      {showNonBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.nonBlockingChoiceNotification?.variant}
          title={textualData?.nonBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.nonBlockingChoiceNotification?.content} />}
        />
      )}

      {!showBlockingNotification && <NextButton isLoading={isSubmitSuccessful}>{textualData?.nextStepText}</NextButton>}

      {showBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.errorBlockingChoiceNotification?.variant}
          title={textualData?.errorBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.errorBlockingChoiceNotification?.content} />}
        />
      )}
    </Layout.Content>
  );
};
